import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Package, Wrench, DollarSign, RefreshCw } from 'lucide-react';
import { ApprovalTable } from '@/components/approvals/ApprovalTable';
import { ApprovalActionDialog } from '@/components/approvals/ApprovalActionDialog';
import {
  useApprovals,
  useApprove,
  useReject,
  useMarkPurchased,
  useConfirmComputerNeeded,
  useConfirmNoComputerNeeded,
  useSyncFutureHires,
  useOrderFromQuipteams,
  useCancelApproval,
} from '@/hooks/useApprovals';
import type { ApprovalForPurchase } from '../../../../shared/types';
import { usePagination } from '@/hooks/usePagination';

type ActionType = 'approve' | 'reject' | 'markPurchased' | 'confirmNeeded' | 'confirmNotNeeded' | 'orderFromQuipteams' | 'cancel';

export function ApprovalForPurchasePage() {
  const { t } = useTranslation();
  const [selectedApproval, setSelectedApproval] = useState<ApprovalForPurchase | null>(null);
  const [dialogAction, setDialogAction] = useState<ActionType | null>(null);

  // Fetch approvals for each workflow stage
  const { data: draftApprovals = [], isLoading: draftLoading, refetch: refetchDraft } = useApprovals({ status: 'DRAFT' });
  const { data: pendingApprovals = [], isLoading: pendingLoading, refetch: refetchPending } = useApprovals({ status: 'PENDING' });
  const { data: approvedApprovals = [], isLoading: approvedLoading, refetch: refetchApproved } = useApprovals({ status: 'APPROVED' });
  const { data: purchasedRaw = [], isLoading: purchasedLoading, refetch: refetchPurchased } = useApprovals({ status: 'PURCHASED' });
  const purchasedApprovals = useMemo(() => [...purchasedRaw].sort((a, b) =>
    (b.purchaseCompletedAt ?? b.createdAt).localeCompare(a.purchaseCompletedAt ?? a.createdAt)
  ), [purchasedRaw]);
  // Paginado solo en Completadas: es el historial de compras y crece para siempre. Las otras
  // tres tabs son colas de trabajo (se vacían al avanzar). El contador de la card sigue sobre el total.
  const { pageItems: pagePurchased, footer: purchasedFooter } = usePagination(purchasedApprovals, 25);

  // Contexto por request (oficina/stock/sugerencia de modelo) — un solo fetch
  const { data: contextData } = trpc.approvals.getApprovalsContext.useQuery(undefined, {
    staleTime: 60_000,
  });
  const approvalsContext = contextData?.context;

  const approveMutation = useApprove();
  const rejectMutation = useReject();
  const markPurchasedMutation = useMarkPurchased();
  const confirmNeededMutation = useConfirmComputerNeeded();
  const confirmNotNeededMutation = useConfirmNoComputerNeeded();
  const syncMutation = useSyncFutureHires();
  const orderFromQuiptreamsMutation = useOrderFromQuipteams();
  const cancelMutation = useCancelApproval();

  const refetchAll = () => {
    refetchDraft();
    refetchPending();
    refetchApproved();
    refetchPurchased();
  };

  const handleSync = async () => {
    await syncMutation.mutateAsync();
    refetchAll();
  };

  // Calculate stats
  const stats = {
    draft: draftApprovals.length,
    pending: pendingApprovals.length,
    approved: approvedApprovals.length,
    purchased: purchasedApprovals.length,
  };

  // Tech workflow handlers
  const handleConfirmNeeded = (approval: ApprovalForPurchase) => {
    setSelectedApproval(approval);
    setDialogAction('confirmNeeded');
  };

  const handleConfirmNotNeeded = (approval: ApprovalForPurchase) => {
    setSelectedApproval(approval);
    setDialogAction('confirmNotNeeded');
  };

  // Finance workflow handlers
  const handleApprove = (approval: ApprovalForPurchase) => {
    setSelectedApproval(approval);
    setDialogAction('approve');
  };

  const handleReject = (approval: ApprovalForPurchase) => {
    setSelectedApproval(approval);
    setDialogAction('reject');
  };

  const handleOrderFromQuipteams = (approval: ApprovalForPurchase) => {
    setSelectedApproval(approval);
    setDialogAction('orderFromQuipteams');
  };

  const handleCancel = (approval: ApprovalForPurchase) => {
    setSelectedApproval(approval);
    setDialogAction('cancel');
  };

  const handleConfirmAction = async (data: any) => {
    if (!selectedApproval) return;

    try {
      if (dialogAction === 'confirmNeeded') {
        await confirmNeededMutation.mutateAsync({
          approvalId: selectedApproval.approvalId,
          ...data,
        });
      } else if (dialogAction === 'confirmNotNeeded') {
        await confirmNotNeededMutation.mutateAsync({
          approvalId: selectedApproval.approvalId,
        });
      } else if (dialogAction === 'approve') {
        await approveMutation.mutateAsync({
          approvalId: selectedApproval.approvalId,
          notes: data.notes,
          financeAssignedName: data.financeAssignedName,
        });
      } else if (dialogAction === 'reject') {
        await rejectMutation.mutateAsync({
          approvalId: selectedApproval.approvalId,
          notes: data.notes,
        });
      } else if (dialogAction === 'markPurchased') {
        await markPurchasedMutation.mutateAsync({
          approvalId: selectedApproval.approvalId,
        });
      } else if (dialogAction === 'orderFromQuipteams') {
        await orderFromQuiptreamsMutation.mutateAsync({
          approvalId: selectedApproval.approvalId,
          kitId: data.kitId,
          kitName: data.kitName,
          recipientPhone: data.recipientPhone,
          recipientAddress: data.recipientAddress,
          recipientCountry: data.recipientCountry,
        });
      } else if (dialogAction === 'cancel') {
        await cancelMutation.mutateAsync({
          approvalId: selectedApproval.approvalId,
          reason: data.reason,
        });
      }

      // Close dialog and refresh
      setDialogAction(null);
      setSelectedApproval(null);
      refetchAll();
    } catch (error) {
      console.error('Error performing action:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t('approvals.page.title')}</h2>
          <p className="text-muted-foreground">
            {t('approvals.page.description')}
          </p>
        </div>
        <Button
          onClick={handleSync}
          disabled={syncMutation.isPending}
          variant="outline"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
          {syncMutation.isPending ? t('approvals.page.syncing') : t('approvals.page.sync_now')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-3 md:grid-cols-4">
        <Card className="p-4 border-l-4 border-l-blue-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('approvals.page.stats.tech_review')}</p>
              <p className="text-3xl font-bold mt-1">{stats.draft}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('approvals.page.stats.tech_review_helper')}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
              <Wrench className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card className="p-4 border-l-4 border-l-yellow-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('approvals.page.stats.finance_review')}</p>
              <p className="text-3xl font-bold mt-1">{stats.pending}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('approvals.page.stats.finance_review_helper')}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30">
              <DollarSign className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
        </Card>

        <Card className="p-4 border-l-4 border-l-green-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('approvals.page.stats.approved')}</p>
              <p className="text-3xl font-bold mt-1">{stats.approved}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('approvals.page.stats.approved_helper')}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>

        <Card className="p-4 border-l-4 border-l-purple-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t('approvals.page.stats.purchased')}</p>
              <p className="text-3xl font-bold mt-1">{stats.purchased}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('approvals.page.stats.purchased_helper')}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30">
              <Package className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Tabbed Interface */}
      <Tabs defaultValue="tech" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tech" className="gap-2">
            <Wrench className="h-4 w-4" />
            {t('approvals.page.tabs.tech')}
            {stats.draft > 0 && (
              <Badge variant="destructive" className="ml-1">
                {stats.draft}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="finance" className="gap-2">
            <DollarSign className="h-4 w-4" />
            {t('approvals.page.tabs.finance')}
            {stats.pending > 0 && (
              <Badge variant="destructive" className="ml-1">
                {stats.pending}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved" className="gap-2">
            <CheckCircle className="h-4 w-4" />
            {t('approvals.page.tabs.approved')}
            {stats.approved > 0 && (
              <Badge variant="default" className="ml-1">
                {stats.approved}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="completed" className="gap-2">
            <Package className="h-4 w-4" />
            {t('approvals.page.tabs.completed')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tech">
          <Card>
            <ApprovalTable
              approvals={draftApprovals}
              isLoading={draftLoading}
              onConfirmNeeded={handleConfirmNeeded}
              onConfirmNotNeeded={handleConfirmNotNeeded}
              onCancel={handleCancel}
              workflowStage="tech"
              context={approvalsContext}
            />
          </Card>
        </TabsContent>

        <TabsContent value="finance">
          <Card>
            <ApprovalTable
              approvals={pendingApprovals}
              isLoading={pendingLoading}
              onApprove={handleApprove}
              onReject={handleReject}
              onCancel={handleCancel}
              workflowStage="finance"
              context={approvalsContext}
            />
          </Card>
        </TabsContent>

        <TabsContent value="approved">
          <Card>
            <ApprovalTable
              approvals={approvedApprovals}
              isLoading={approvedLoading}
              onOrderFromQuipteams={handleOrderFromQuipteams}
              onCancel={handleCancel}
              workflowStage="approved"
            />
          </Card>
        </TabsContent>

        <TabsContent value="completed">
          <Card>
            <ApprovalTable
              approvals={pagePurchased}
              isLoading={purchasedLoading}
              workflowStage="completed"
            />
            {purchasedFooter && <div className="px-4 pb-3">{purchasedFooter}</div>}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Action Dialog */}
      {dialogAction && (
        <ApprovalActionDialog
          open={!!dialogAction}
          onOpenChange={(open) => {
            if (!open) {
              setDialogAction(null);
              setSelectedApproval(null);
            }
          }}
          approval={selectedApproval}
          actionType={dialogAction}
          onConfirm={handleConfirmAction}
          isLoading={
            approveMutation.isPending ||
            rejectMutation.isPending ||
            markPurchasedMutation.isPending ||
            confirmNeededMutation.isPending ||
            confirmNotNeededMutation.isPending ||
            orderFromQuiptreamsMutation.isPending ||
            cancelMutation.isPending
          }
        />
      )}
    </div>
  );
}
